import {ErrorHandler, Injectable, inject, signal} from '@angular/core';
import { DICTIONARY } from './translation.data';
import { catchError, tap } from 'rxjs/operators';
import { CmsApiService } from '../api/cms-api.service';
import { LanguageService } from '../language.service';
import { of } from 'rxjs';
import { MissingTranslateError } from '../../shared/errors/missing-translate';
import { isNullOrUndefined } from '../../helpers/utils';
import {TranslationType} from "ngx-unificator/types/cms";

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private _cmsApi = inject(CmsApiService);
  private _language = inject(LanguageService);
  private _errorHandler = inject(ErrorHandler);


  /**
   * Available languages
   */
  private _dictionary: any = DICTIONARY;

  /**
   * Missing translations storage
   * For preventing missing translation error logs duplicating
   */
  private _missingTranslates: Set<string> = new Set();

  public loaded = signal<boolean>(false);

  /**
   * Translate text
   *
   * Using: translate('Hello, #name#', {name: 'Alex'});
   * Output: 'Hei Alex'; // (fi lang)
   *
   * @param key
   * @param params
   * @param defaultValue
   */
  public translate(key: any, params = null, defaultValue = null): string {
    let translated = '';
    let targetLanguage = this._language.current;

    if (isNullOrUndefined(this._dictionary[targetLanguage])) {
      targetLanguage = this._language.previous;
    }

    if (isNullOrUndefined(this._dictionary[targetLanguage]) || isNullOrUndefined(this._dictionary[targetLanguage][key])) {
      translated = defaultValue ?? key;

      if (!this._missingTranslates.has(key) && DICTIONARY[targetLanguage]) {
        this._missingTranslates.add(key);
        this._errorHandler.handleError(new MissingTranslateError(key));
      }
    } else {
      translated = this._dictionary[targetLanguage][key];
    }

    if (params) {
      const keyParams = [];
      const regex = /\#([^^]*?)\#/ig;
      let match: any;
      // tslint:disable-next-line:no-conditional-assignment
      while (match = regex.exec(translated)) {
        keyParams.push(match[1]);
      }
      keyParams.forEach((item) => {
        // @ts-ignore
        translated = translated.replace(`#${item}#`, params[item]);
      });
    }

    return translated;
  }

  /**
   * Load user translates
   */
  public loadTranslates() {
    this.loaded.set(false);
    return this._cmsApi.localizationList().pipe(
      catchError(error => of({data: []})),
      tap((res: {data: TranslationType[]}) => {
        this._resolveLangFormat(res);
        this.loaded.set(true);
      }),
    );
  }

  /**
   * Resolve lang format
   * @param res
   */
  private _resolveLangFormat(res: any) {
    if (!res.data) {
      return;
    }

    const keyValuePairs: any = {};
    res.data.forEach((item: any) => {
      if (!isNullOrUndefined(item.key) && !isNullOrUndefined(item.value)) {
        keyValuePairs[item.key] = item.value;
      }
    });
    DICTIONARY[this._language.current] = keyValuePairs;
  }
}
